/**
 * Breadcrumb - Navigation Trail Component
 * 
 * Shows the user's current location in the app hierarchy.
 */

import * as React from "react"
import { Link } from "react-router-dom"
import { ChevronRightIcon, HomeIcon } from "@heroicons/react/24/outline"
import { cn } from "../../shared/lib/cn"

export interface BreadcrumbTrailItem {
  label: React.ReactNode
  path?: string
  isCurrent?: boolean
}

function isInternalPath(path: string) {
  return path.startsWith("/") && !path.startsWith("//")
}

/* ============================================
   BREADCRUMB CONTAINER
   ============================================ */

interface BreadcrumbProps extends React.HTMLAttributes<HTMLElement> {
  /** Separator between items */
  separator?: React.ReactNode
  /** Show home icon as first item */
  showHome?: boolean
  /** Home href */
  homeHref?: string
}

interface BreadcrumbTrailProps extends Omit<React.HTMLAttributes<HTMLElement>, "children"> {
  items: BreadcrumbTrailItem[]
  separator?: React.ReactNode
}

const BreadcrumbTrail = React.forwardRef<HTMLElement, BreadcrumbTrailProps>(
  ({ className, items, separator, ...props }, ref) => {
    if (!items.length) return null

    return (
      <Breadcrumb
        ref={ref}
        separator={separator}
        className={cn("min-w-0", className)}
        {...props}
      >
        {items.map((item, index) => (
          <BreadcrumbItem
            key={index}
            href={item.path}
            isCurrent={item.isCurrent ?? index === items.length - 1}
          >
            {item.label}
          </BreadcrumbItem>
        ))}
      </Breadcrumb>
    )
  }
)
BreadcrumbTrail.displayName = "BreadcrumbTrail"

const Breadcrumb = React.forwardRef<HTMLElement, BreadcrumbProps>(
  ({ className, separator, showHome = false, homeHref = "/", children, ...props }, ref) => {
    const items = React.Children.toArray(children)
    
    return (
      <nav
        ref={ref}
        aria-label="Breadcrumb"
        className={cn("flex items-center", className)}
        {...props}
      >
        <ol className="flex min-w-0 items-center gap-1.5">
          {showHome && (
            <>
              <li>
                <Link
                  to={homeHref}
                  className={cn(
                    "flex items-center justify-center w-6 h-6 rounded",
                    "text-gray-400 hover:text-gray-600",
                    "dark:text-gray-500 dark:hover:text-gray-300",
                    "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-300"
                  )}
                >
                  <HomeIcon className="w-4 h-4" />
                </Link>
              </li>
              <li aria-hidden="true" className="text-gray-300 dark:text-gray-600">
                {separator || <ChevronRightIcon className="w-4 h-4" />}
              </li>
            </>
          )}
          {items.map((child, index) => (
            <React.Fragment key={index}>
              <li className="flex min-w-0 items-center">{child}</li>
              {index < items.length - 1 && (
                <li aria-hidden="true" className="text-gray-300 dark:text-gray-600">
                  {separator || <ChevronRightIcon className="w-4 h-4" />}
                </li>
              )}
            </React.Fragment>
          ))}
        </ol>
      </nav>
    )
  }
)
Breadcrumb.displayName = "Breadcrumb"

/* ============================================
   BREADCRUMB ITEM
   ============================================ */

interface BreadcrumbItemProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Link href (omit for current page) */
  href?: string
  /** Is this the current/active page */
  isCurrent?: boolean
  /** Custom icon */
  icon?: React.ReactNode
}

const BreadcrumbItem = React.forwardRef<HTMLSpanElement, BreadcrumbItemProps>(
  ({ className, href, isCurrent = false, icon, children, ...props }, ref) => {
    const content = (
      <>
        {icon && <span className="mr-1.5 flex-none">{icon}</span>}
        <span className="truncate">{children}</span>
      </>
    )

    if (href && !isCurrent) {
      return (
        <span ref={ref} {...props}>
          {isInternalPath(href) ? (
            <Link
              to={href}
              className={cn(
                "flex min-w-0 items-center rounded-sm text-sm font-medium",
                "text-gray-500 hover:text-gray-700",
                "dark:text-gray-400 dark:hover:text-gray-200",
                "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-300",
                className
              )}
            >
              {content}
            </Link>
          ) : (
            <a
              href={href}
              className={cn(
                "flex min-w-0 items-center rounded-sm text-sm font-medium",
                "text-gray-500 hover:text-gray-700",
                "dark:text-gray-400 dark:hover:text-gray-200",
                "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-300",
                className
              )}
            >
              {content}
            </a>
          )}
        </span>
      )
    }

    return (
      <span
        ref={ref}
        aria-current={isCurrent ? "page" : undefined}
        className={cn(
          "flex min-w-0 items-center text-sm font-medium",
          isCurrent
            ? "text-charcoal dark:text-white"
            : "text-gray-500 dark:text-gray-400",
          className
        )}
        {...props}
      >
        {content}
      </span>
    )
  }
)
BreadcrumbItem.displayName = "BreadcrumbItem"

/* ============================================
   BREADCRUMB ELLIPSIS (for collapsed items)
   ============================================ */

interface BreadcrumbEllipsisProps extends React.HTMLAttributes<HTMLSpanElement> {}

const BreadcrumbEllipsis = React.forwardRef<HTMLSpanElement, BreadcrumbEllipsisProps>(
  ({ className, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={cn(
          "flex items-center justify-center w-6 h-6",
          "text-gray-400 dark:text-gray-500",
          "cursor-default",
          className
        )}
        {...props}
      >
        <span className="tracking-widest">...</span>
      </span>
    )
  }
)
BreadcrumbEllipsis.displayName = "BreadcrumbEllipsis"

/* ============================================
   EXPORTS
   ============================================ */

export { Breadcrumb, BreadcrumbTrail, BreadcrumbItem, BreadcrumbEllipsis }
export type { BreadcrumbProps, BreadcrumbTrailProps, BreadcrumbItemProps, BreadcrumbEllipsisProps }
